import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './DashboardPage.css';

interface DashboardStats {
  totalInstances?: number;
  activeInstances?: number;
  validatedInstances?: number;
  totalCustomers?: number;
}

export const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [bannerMessage, setBannerMessage] = useState<string | null>(searchParams.get('message'));

  useEffect(() => {
    if (searchParams.has('message')) {
      searchParams.delete('message');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch customers with their instances
      const customers = (user) ? await api.getInstancesByCustomer(user.customers) : [];
      
      // Calculate stats
      const totalInstances = customers.reduce(
        (sum, customer) => sum + customer.instances.length,
        0
      );
      
      const validatedInstances = customers.reduce(
        (sum, customer) => 
          sum + customer.instances.filter(i => i.validated).length,
        0
      );

      const activeInstances = customers.reduce(
        (sum, customer) => 
          sum + customer.instances.filter(i => i.validated && i.frequencyInMinutes > 0).length,
        0
      );

      setStats({
        totalInstances,
        activeInstances,
        validatedInstances,
        totalCustomers: customers.length,
      });
    } catch (err) {
      setError('Failed to load dashboard data. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <div className="error">
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {bannerMessage && (
        <div className="dashboard-banner">
          <span>{bannerMessage}</span>
          <button className="banner-dismiss" onClick={() => setBannerMessage(null)}>&times;</button>
        </div>
      )}

      <div className="page-header">
        <h1>Dashboard</h1>
        <p className="subtitle">Welcome back, {user?.firstName || user?.username || 'User'}!</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#e3f2fd' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalCustomers || 0}</div>
            <div className="stat-label">Total Customers</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#f3e5f5' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7b1fa2" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
              <line x1="8" y1="21" x2="16" y2="21"></line>
              <line x1="12" y1="17" x2="12" y2="21"></line>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalInstances || 0}</div>
            <div className="stat-label">Total Instances</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#e8f5e9' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#388e3c" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.validatedInstances || 0}</div>
            <div className="stat-label">Validated</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fff3e0' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f57c00" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeInstances || 0}</div>
            <div className="stat-label">Active Syncing</div>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h2 style={{ color: '#fff' }}>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/instances" className="action-card">
            <div className="action-icon">📋</div>
            <h3>View Instances</h3>
            <p>Manage your QuickBooks Desktop instances</p>
          </Link>

          <Link to="/profile" className="action-card">
            <div className="action-icon">👤</div>
            <h3>My Profile</h3>
            <p>Update your account information</p>
          </Link>

          <div className="action-card" onClick={() => alert('Coming soon!')}>
            <div className="action-icon">📊</div>
            <h3>Reports</h3>
            <p>View transaction reports and analytics</p>
          </div>

          <div className="action-card" onClick={() => alert('Coming soon!')}>
            <div className="action-icon">⚙️</div>
            <h3>Settings</h3>
            <p>Configure your preferences</p>
          </div>
        </div>
      </div>

      <div className="info-section">
        <div className="info-card">
          <h3>About QuickBooks Desktop Connect</h3>
          <p>
            QBDC helps you integrate QuickBooks Desktop Enterprise edition with other 
            applications and automation processes using a robust set of APIs. Monitor 
            transactions, manage instances, and streamline your workflows.
          </p>
        </div>
      </div>
    </div>
  );
};
